<template>
  <layout-content-vertical-nav :nav-menu-items="navMenuItemsToRender" >
    <slot></slot>

    <!-- Slot: Navbar -->
    <template #navbar="{ isVerticalNavMenuActive, toggleVerticalNavMenuActive }">
      <div
        class="navbar-content-container"
        :class="{'expanded-search': shallShowFullSearch}"
      >
        <!-- Left Content: Search -->
        <div class="d-flex align-center">
          <v-btn
            tabindex="0"
            v-if="!isVerticalNavMenuActive"
            id="menuButton"
            icon
            @click="toggleVerticalNavMenuActive"
            aria-label="menu-toggle"
          >
            <v-icon
              class=" "
            >
              {{ icons.mdiMenu }}
            </v-icon>
          </v-btn>
        </div>

        <!-- Right Content: Help, Notification & User Dropdown (theme + language live inside the user menu) -->
        <div class="d-flex align-center right-row">
          <connectivity-chip :show-pending="false"></connectivity-chip>
          <offline-lock-toggle></offline-lock-toggle>
          <app-bar-help-menu></app-bar-help-menu>
          <app-bar-notification></app-bar-notification>
          <app-bar-user-menu></app-bar-user-menu>
        </div>
      </div>
    </template>

    <!-- Slot: Footer -->
    <template #footer>
      <div class="d-flex justify-space-between">
        <span style="font-size: 16px !important;">
          &copy; {{ new Date().getFullYear() }}
          <a
            href="https://perkins.org"
            class="text-decoration-none"
          >
            Perkins Scool for the Blind
          </a>
          <span class="d-none d-md-inline">, {{$t('common.page_rights')}}</span>
          {{$t('common.powered_by')}}
          <a
            href="https://innexus.ar"
            class="text-decoration-none"
          >
            Innexus
          </a>.
        </span>
        <div class="align-center d-none d-md-flex">
        </div>
      </div>
    </template>
  </layout-content-vertical-nav>
</template>

<script>
import LayoutContentVerticalNav from '@/@core/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue'
import navMenuItems from '@/navigation/vertical/index-for-coordinator'
import navMenuItemsForAdmin from '@/navigation/vertical/index-for-admin'
import navMenuItemsForSuperAdmin from '@/navigation/vertical/index-for-super-admin'
import navMenuItemsForSchoolRep from '@/navigation/vertical/index-for-school-rep'

// App Bar Components
import AppBarSearch from '@core/layouts/components/app-bar/AppBarSearch.vue'
import AppBarUserMenu from '@/components/AppBarUserMenu.vue'
import AppBarHelpMenu from '@/components/AppBarHelpMenu.vue'
import AppBarNotification from '@core/layouts/components/app-bar/AppBarNotification.vue'
import ConnectivityChip from '@/components/ConnectivityChip.vue'
import OfflineLockToggle from '@/components/OfflineLockToggle.vue'

import { mdiMenu, mdiHeartOutline, mdiChevronRight } from '@mdi/js'

import { getVuetify } from '@core/utils'

// Search Data
import appBarSearchData from '@/assets/app-bar-search-data'

import { ref, watch } from '@vue/composition-api'

export default {
  components: {
    LayoutContentVerticalNav,

    // App Bar Components
    AppBarSearch,
    AppBarUserMenu,
    AppBarHelpMenu,
    AppBarNotification,
    ConnectivityChip,
    OfflineLockToggle,
  },

  methods: {

  },
  setup() {
    const $vuetify = getVuetify()

    // Search
    const appBarSearchQuery = ref('')
    const shallShowFullSearch = ref(false)
    const maxItemsInGroup = 5
    const totalItemsInGroup = ref({
      pages: 0,
      files: 0,
      contacts: 0,
    })
    watch(appBarSearchQuery, () => {
      totalItemsInGroup.value = {
        pages: 0,
        files: 0,
        contacts: 0,
      }
    })

    // NOTE: Update search function according to your usage
    const searchFilterFunc = (item, queryText, itemText) => {
      if (item.header || item.divider) return true

      const itemGroup = (() => {
        if (item.to !== undefined) return 'pages'
        if (item.size !== undefined) return 'files'
        if (item.email !== undefined) return 'contacts'

        return null
      })()

      const isMatched = itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1

      if (isMatched) {
        if (itemGroup === 'pages') totalItemsInGroup.value.pages += 1
        else if (itemGroup === 'files') totalItemsInGroup.value.files += 1
        else if (itemGroup === 'contacts') totalItemsInGroup.value.contacts += 1
      }

      return appBarSearchQuery.value && isMatched && totalItemsInGroup.value[itemGroup] <= maxItemsInGroup
    }

    // ? Handles case where in <lg vertical nav menu is open and search is triggered using hotkey then searchbox is hidden behind vertical nav menu overlaty
    const handleShallShowFullSearchUpdate = (isVerticalNavMenuActive, toggleVerticalNavMenuActive) => {
      if ($vuetify.breakpoint.mdAndDown && isVerticalNavMenuActive) {
        toggleVerticalNavMenuActive()
      }
    }

    let navMenuItemsToRender = navMenuItems
    const session = localStorage.getItem('session') || sessionStorage.getItem('session')
    if (session) {
      const parsedSession = JSON.parse(session)
      if (parsedSession.user.role === 0 || parsedSession.user.role === 1 || parsedSession.user.role === 4) {
        if (parsedSession.user.is_superuser) {
          navMenuItemsToRender = navMenuItemsForSuperAdmin
        } else {
          navMenuItemsToRender = navMenuItemsForAdmin
        }
      } else if (parsedSession.user.role === 3) {
        navMenuItemsToRender = navMenuItemsForSchoolRep
      }
      // The "Regions" (country grouping) screen is admin-only (role 0). Roles 1 and 4
      // share the admin nav, so hide the item from them (backend also enforces 403).
      // Same for the offline changes archive: admin + superuser only (the
      // super-admin nav also goes to superusers with roles 1 and 4).
      if (parsedSession.user.role !== 0) {
        navMenuItemsToRender = navMenuItemsToRender.filter(item => item.to !== 'regions' && item.to !== 'offline-payloads')
      }
    }

    return {
      navMenuItems,
      navMenuItemsToRender,
      handleShallShowFullSearchUpdate,

      // Search
      appBarSearchQuery,
      shallShowFullSearch,
      appBarSearchData,
      searchFilterFunc,

      icons: {
        mdiMenu,
        mdiHeartOutline,
        mdiChevronRight
      },
    }
  },
}
</script>

<style lang="scss" scoped>

.navbar-content-container {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  position: relative;
  font-family: "AvenirNext";
}

// ? Handle bg of autocomplete for blured appBar
.v-app-bar.bg-blur {
  .expanded-search {
    ::v-deep .app-bar-autocomplete-box div[role='combobox'] {
      background-color: transparent;
    }

    > .d-flex > button.v-icon {
      display: none;
    }

    // ===

    & > .right-row {
      visibility: hidden;
      opacity: 0;
    }

    ::v-deep .app-bar-search-toggler {
      visibility: hidden;
    }
  }
}
</style>
